/*
 * File: DeliveryTypeCell.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 10 Jan 2022 2:59:44 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Form, Select, Tooltip } from 'antd';
import { useField } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetDeliveryTypesQuery } from 'redux/services/useGrannyFannyNesselrodeService';

interface DeliveryTypeCellProps {
  deliveryType: SalesOrderPayloadModel['lines'][number]['deliveryType'];
  index: number;
}

const styles: InlineStylesModel = {
  select: {
    width: '100%'
  },
  formItem: {
    marginBottom: 0
  }
};

export const DeliveryTypeCell = React.memo(({ index, deliveryType }: DeliveryTypeCellProps): JSX.Element => {
  const form = Form.useFormInstance();
  const [x, _, { setValue }] = useField(`lines.${index}.deliveryType`);


  /* ****************** API Hooks ****************** */
  const { data, isLoading } = useGetDeliveryTypesQuery();
  const { pathname } = useLocation();

  /* ****************** JSX / Variables ****************** */
  const options = data?.data.map((types) => ({ value: types.name, label: types.name.toUpperCase() }));

  const isPosted = pathname.includes('/posted');

  /* ****************** Functions ****************** */
  const handleSelect = (value: string): void => {
    setValue(value);
    form.setFieldValue('deliveryType', value);
  };

  useEffect(() => {
    if (!deliveryType) setValue('stock');
  }, [deliveryType]);

  const isInvalid = useMemo(() => (deliveryType && !deliveryType.match(/stock|STOCK|Stock/g) ? true : false), [deliveryType]);

  const errorMessage = useMemo(() => (deliveryType && !deliveryType.match(/stock|STOCK|Stock/g) ? 'delivery type must be stock' : 'Unable to report the issue'), [deliveryType]);

  /* ****************** Render ****************** */
  return (
    <Tooltip title={isInvalid ? errorMessage : undefined}>
      <Select
        loading={isLoading}
        disabled={isPosted}
        value={form.getFieldValue('deliveryType')}
        onSelect={handleSelect}
        options={options}
        style={styles.select}
        status={isInvalid ? 'error' : undefined}
      />
    </Tooltip>
  );
});
